import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { EmployeeService } from '../services/employee.service';
import { CashRegisterService } from '../services/cash-register.service';
import { FancySnackbarService } from '../../shared/services/fancy-snackbar.service';
import { firstValueFrom } from 'rxjs';

/**
 * Guest Guard - Only allows unauthenticated users
 * Redirects authenticated users to dashboard (or platform-admin for admins)
 * Use for: login, register, invite, join routes
 */
export const guestGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (authService.isAuthenticated()) {
    // Redirect authenticated users to appropriate page
    if (authService.isPlatformAdmin()) {
      router.navigate(['/platform-admin']);
    } else {
      router.navigate(['/dashboard']);
    }
    return false;
  }

  // Allow unauthenticated users
  return true;
};

/**
 * Auth Guard - Basic authentication check
 * Only allows access to authenticated users (either platform admin or regular user)
 */
export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForAuth();

  if (authService.isAuthenticated()) {
    return true;
  }

  router.navigate(['/login'], { queryParams: { returnUrl: state.url } });
  return false;
};

/**
 * Business User Guard
 * Only allows access to regular business users (not platform admins)
 */
export const businessUserGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const employeeService = inject(EmployeeService);
  const router = inject(Router);
  const snackBar = inject(FancySnackbarService);

  await authService.waitForAuth();

  // Must be authenticated
  if (!authService.isAuthenticated()) {
    router.navigate(['/login']);
    return false;
  }

  // Must NOT be a platform admin
  if (authService.isPlatformAdmin()) {
    router.navigate(['/platform-admin']);
    return false;
  }

  // Must have a current business selected
  const user = authService.currentUser();
  if (!user?.currentBusinessId) {
    router.navigate(['/select-business']);
    return false;
  }

  // Check if employee is active — use the cached signal from EmployeeService
  // instead of making a fresh Firestore read on every route navigation.
  try {
    const employee = await employeeService.waitForCurrentEmployee();
    if (employee && employee.active === false) {
      await authService.logout();
      router.navigate(['/login']);
      snackBar.error(
        'Tu cuenta está inactiva. Contacta al administrador de tu negocio.',
        'Cerrar',
        { duration: 8000 }
      );
      return false;
    }
  } catch (e) {
    console.error('Error checking employee status:', e);
  }

  return true;
};

/**
 * Platform Admin Guard
 * Only allows access to platform admins
 */
export const platformAdminGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth to be ready
  await authService.waitForAuth();

  // Check if user is a platform admin
  if (authService.isPlatformAdmin()) {
    return true;
  }

  // Not a platform admin - redirect to appropriate page
  if (authService.isAuthenticated()) {
    // Regular user trying to access platform admin
    router.navigate(['/dashboard']);
  } else {
    // Not logged in at all
    router.navigate(['/login']);
  }

  return false;
};

/**
 * Business Selection Guard
 * Redirects users with single business to dashboard
 * Shows business selector for users with multiple businesses
 */
export const businessSelectionGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForAuth();

  const user = authService.currentUser();

  if (!user) {
    router.navigate(['/login']);
    return false;
  }

  // If user has only one business, auto-select and redirect
  if (user.businesses && user.businesses.length === 1) {
    const businessId = user.businesses[0].businessId;
    await authService.selectBusiness(businessId);
    router.navigate(['/dashboard']);
    return false;
  }

  // Allow access to business selector
  return true;
};

/**
 * POS Guard
 * Checks if the cashierId query param refers to an open cash register.
 * If no cashierId is provided or the cash register is closed, redirects to cash-registers page.
 */
export const posGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const cashRegisterService = inject(CashRegisterService);
  const router = inject(Router);

  await authService.waitForAuth();

  // Must be authenticated with a business
  if (!authService.isAuthenticated() || !authService.currentUser()?.currentBusinessId) {
    router.navigate(['/login']);
    return false;
  }

  // Get cashierId from query params
  const cashierId = route.queryParams['cashierId'];

  if (!cashierId) {
    // No cashier ID provided, redirect to cash registers to select one
    router.navigate(['/cash-registers']);
    return false;
  }

  try {
    // Check if the cash register exists and is open
    const cashRegister = await firstValueFrom(cashRegisterService.getCashRegisterById(cashierId));

    if (!cashRegister) {
      console.warn(`Cash register ${cashierId} not found`);
      router.navigate(['/cash-registers']);
      return false;
    }

    if (cashRegister.status !== 'open') {
      console.warn(`Cash register ${cashierId} is closed`);
      router.navigate(['/cash-registers']);
      return false;
    }

    // Cash register is open, allow access
    return true;
  } catch (error) {
    console.error('Error checking cash register:', error);
    router.navigate(['/cash-registers']);
    return false;
  }
};
